import { MessageSquare } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { CompactPromptBox } from '@/components/CompactPromptBox';
import { ConversationListItem } from '@/components/ConversationListItem';

// Mock project-specific conversation data
const mockProjectConversations = [
  {
    id: 'conv-1',
    projectName: 'FocusPane Core',
    lastMessage: 'What are the main blockers for the v2.0 release?',
    timestamp: new Date(Date.now() - 1000 * 60 * 45), // 45 mins ago
    unread: true,
  },
  {
    id: 'conv-2',
    projectName: 'FocusPane Core',
    lastMessage: 'Show me the code review metrics for this sprint',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 3), // 3 hours ago
    unread: false,
  },
  {
    id: 'conv-3',
    projectName: 'FocusPane Core',
    lastMessage: 'Help me understand why the test coverage dropped',
    timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2), // 2 days ago
    unread: false,
  },
];

/**
 * ProjectConversationsPage
 *
 * This component is rendered as a tab inside ProjectDetailPage.
 * It does NOT use AppShell wrapper since it's embedded in the parent page.
 */
export default function ProjectConversationsPage({ projectId, projectName }) {
  const handleStartConversation = (message) => {
    console.log('Starting project conversation:', { projectId, message });
    // In real implementation, this would navigate to a new conversation
    // scoped to this specific project
  };

  const hasConversations = mockProjectConversations.length > 0;

  return (
    <div className="space-y-6">
      {/* New Conversation Input */}
      <Card className="glass-elevated p-6">
        <CompactPromptBox
          onSubmit={handleStartConversation}
          placeholder={`Ask about ${projectName || 'this project'}...`}
        />
      </Card>

      {/* Recent Conversations */}
      {hasConversations ? (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Recent Conversations
            </h3>
            <span className="text-xs text-muted-foreground">
              {mockProjectConversations.length} session
              {mockProjectConversations.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="space-y-2">
            {mockProjectConversations.map((conversation) => (
              <ConversationListItem
                key={conversation.id}
                conversation={conversation}
                href={`/conversations/${conversation.id}`}
                showProjectName={false}
              />
            ))}
          </div>
        </div>
      ) : (
        // Empty State
        <Card className="glass-subtle p-12">
          <div className="flex flex-col items-center justify-center text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <MessageSquare className="w-8 h-8 text-primary" />
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-semibold">
                No conversations about this project yet
              </h3>
              <p className="text-muted-foreground max-w-md">
                Start a conversation with Anchor to get insights, identify
                blockers, and understand what's happening in {projectName || 'this project'}.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Quick Actions */}
      {hasConversations && (
        <div className="grid grid-cols-2 gap-3">
          <Card className="glass-subtle p-4 hover:glass-elevated transition-all cursor-pointer">
            <div className="space-y-1">
              <div className="text-sm font-semibold">📊 Sprint Summary</div>
              <p className="text-xs text-muted-foreground">
                Get a comprehensive overview of the current sprint
              </p>
            </div>
          </Card>
          <Card className="glass-subtle p-4 hover:glass-elevated transition-all cursor-pointer">
            <div className="space-y-1">
              <div className="text-sm font-semibold">🚧 Identify Blockers</div>
              <p className="text-xs text-muted-foreground">
                Find and analyze what's blocking progress
              </p>
            </div>
          </Card>
          <Card className="glass-subtle p-4 hover:glass-elevated transition-all cursor-pointer">
            <div className="space-y-1">
              <div className="text-sm font-semibold">📈 Review Metrics</div>
              <p className="text-xs text-muted-foreground">
                Analyze key performance indicators
              </p>
            </div>
          </Card>
          <Card className="glass-subtle p-4 hover:glass-elevated transition-all cursor-pointer">
            <div className="space-y-1">
              <div className="text-sm font-semibold">💡 Get Insights</div>
              <p className="text-xs text-muted-foreground">
                Discover patterns and recommendations
              </p>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
